<template>
    <CoreProductTray
        :id="id"
        ref="coreProductTrayRef"
        :listId="listId"
        :listName="listName"
        :closetListId="closetListId"
        :contentModuleId="contentModuleId"
        :contentTitle="contentTitle"
        :imagePreset="imagePreset"
        :products="products"
        :headerLink="headerLink"
        :headerLinkPlacement="headerLinkPlacement"
        :headerTitle="headerTitle"
        :headerSubtitle="headerSubtitle"
        :totalProducts="totalProducts"
        :mobilePeek="mobilePeek"
        :mobilePeekPercent="mobilePeekPercent"
        :mediumPeekPercent="mediumPeekPercent"
        :displayCounter="displayCounter"
        :desktopSlideSize="desktopSlideSize"
        :xlDesktopSlideSize="xlDesktopSlideSize"
        :mediumSlideSize="mediumSlideSize"
        :mobileSlideSize="mobileSlideSize"
        :isFullBleedDesktop="isFullBleedDesktop"
        :translucentButtons="translucentButtons"
        :lightButtons="lightButtons"
        :lightInfoContainer="lightInfoContainer"
        :catalogResponseId="catalogResponseId"
        :showProductInfo="showProductInfo"
        :showEmptyContentSlot="showEmptyContentSlot"
        :showClosetButtonOnTile="showClosetButtonOnTile"
        :showReviewButtonOnTile="showReviewButtonOnTile"
        :showAddToList="showAddToList"
        :showShopTheLookBanner="showShopTheLookBanner"
        :fillEmptyPageWithBlankTiles="fillEmptyPageWithBlankTiles"
        :carouselItemUniqueKey="carouselItemUniqueKey"
        :analyticsType="analyticsType"
        :viewAllTileLink="viewAllTileLink"
        :faceOutImage="faceOutImage"
        :selectedCuration="selectedCuration"
        :emptyTile="emptyTile"
        :isLoaded="isLoaded"
        :shouldSkeletonLoadHeader="shouldSkeletonLoadHeader"
        :requireMinNumberOfProducts="requireMinNumberOfProducts"
        :trayGroupCurationNavItemSlug="trayGroupCurationNavItemSlug"
        @toggle-save-product-clicked="closetBadgeClicked"
    >
        <template #below-header-content>
            <slot name="below-header-content"></slot>
        </template>
        <template #tile="compProps">
            <CatalogTile v-bind="compProps" />
        </template>
        <template #empty-content>
            <slot name="empty-content"></slot>
        </template>
    </CoreProductTray>
</template>

<script setup>
import { watch, ref } from 'vue';

import { useActions } from '~coreModules/core/js/composables/vuexHelpers';

import { GLOBAL_EVENT } from '~coreModules/core/js/store';
import { IMAGE_PRESETS } from '~modules/core/js/constants';
import { getCatalogTrayAnalyticsData } from '~modules/catalog/js/catalog-utils';

import productTrayProps from '~coreModules/core/js/ui/productTrayProps';
import CoreProductTray from '~coreModules/core/components/ui/trays/CoreProductTray.vue';
import CatalogTile from '~modules/catalog/components/CatalogTile.vue';

const props = defineProps({
    ...productTrayProps,
    products: {
        type: Array,
        required: true,
    },
    totalProducts: {
        type: Number,
        default: 15,
    },
    showEmptyContentSlot: {
        type: Boolean,
        default: false,
    },
    showClosetButtonOnTile: {
        type: Boolean,
        default: true,
    },
    showReviewButtonOnTile: {
        type: Boolean,
        default: false,
    },
    fillEmptyPageWithBlankTiles: {
        type: Boolean,
        default: true,
    },
    lightInfoContainer: {
        type: Boolean,
        default: true,
    },
    catalogResponseId: {
        type: String,
        default: null,
    },
    carouselItemUniqueKey: {
        type: String,
        default: null,
    },
    analyticsType: {
        type: String,
        default: null,
    },
    traceId: {
        type: String,
        default: null,
    },
    closetListId: {
        type: String,
        default: null,
    },
    viewAllTileLink: {
        type: Object,
        default: null,
    },
    selectedCuration: {
        type: String,
        default: '',
    },
    randomizeCurationNavItems: {
        type: Boolean,
        default: false,
    },
    hasEnteredViewport: {
        type: Boolean,
        required: true,
    },
    emptyTile: {
        type: Object,
        default: null,
    },
    isLoaded: {
        type: Boolean,
        default: true,
    },
    shouldSkeletonLoadHeader: {
        type: Boolean,
        default: false,
    },
    requireMinNumberOfProducts: {
        type: Boolean,
        default: false,
    },
    trayGroupCurationNavItemSlug: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['toggle-save-product-clicked']);

const coreProductTrayRef = ref(null);
const loadedNotInView = ref(false);

const imagePreset = IMAGE_PRESETS.productTray;

const { trackGlobalEvent } = useActions({ trackGlobalEvent: GLOBAL_EVENT });

function closetBadgeClicked({ index }) {
    emit('toggle-save-product-clicked', index);
}

function trackViewProductList() {
    if (!props.listName) return;

    trackGlobalEvent(getCatalogTrayAnalyticsData({
        products: props.products,
        catalogResponseId: props.catalogResponseId,
        filters: props.filters,
        traceId: props.traceId,
        listName: props.listName,
        listId: props.listId,
        curation: props.selectedCuration,
        ...(props.selectedCuration && { randomizeCurationNavItems: props.randomizeCurationNavItems }),
        faceOut: props.faceOutImage?.toLowerCase(),
        trayGroupCurationNavItemSlug: props.trayGroupCurationNavItemSlug,
    }));
}

watch(() => props.products.length, (newValue, oldValue) => {
    if (!props.hasEnteredViewport && newValue > 0) {
        loadedNotInView.value = true;
        return;
    }

    if (!oldValue && newValue > 0 && !loadedNotInView.value) {
        trackViewProductList();
    }
}, { immediate: true });

watch(() => props.hasEnteredViewport, (newValue, oldValue) => {
    if (!oldValue && newValue && loadedNotInView.value) {
        trackViewProductList();
    }
}, { immediate: true });

defineExpose({
    resetCarousel: () => coreProductTrayRef.value?.resetCarousel(),
});
</script>
